import React, { Fragment, useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { withRouter } from 'react-router';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import { toast } from 'react-toastify';
import { useForceUpdate } from '../../../../utils/hooks';
import IconToast from '../../../NewToast';
import { vertexGenerateWithTextStreaming } from '../../../../store/vertex/api';
import { hasCampaignCode } from '../../../../utils/user';
import { trackDirect } from '../../../../store/analytics/api';
import { tracking } from '../../../../utils/tracking';
import { updateFactSheet } from '../../../../store/client/duck';
import { getTraitsFromDisc } from '../Inputs/Inputs';
import { MatchLazyPacks } from './views';

export * from './components';
export * from './views';

const IdentifierWhitelist = [
  'match_hot_topic:product:client',
  'match_hot_topic:product:client:cbb',
  'match_hot_topic:meeting_notes:client',
  'match_hot_topic:meeting_notes:client:cbb',
  'match_hot_topic:general',
  'match_hot_topic:general:client',
  'match_hot_topic:general:cbb',
  'match_hot_topic:general:client:cbb'
];

const defaultFormValues = {
  gender: '',
  lifeStage: '',
  target: [],
  interested: [],
  characters: [],
  hobbies: [],
  chatPurpose: ''
};

const ChatGPTPersonalAnalysis = props => {
  const {
    intl,
    history,
    ChatGPTPersonalAnalysis,
    toggleControl,
    userDetails,
    teamCampaignCodes,
    team,
    updateFactSheet
  } = props;
  const [loading, setLoading] = useState(false);
  const { client, closed, type, payload } = ChatGPTPersonalAnalysis || {};

  const forceUpdate = useForceUpdate();
  const initState = history.location.state || {};
  const [formValues, setFormValues] = useState({
    ...defaultFormValues,
    ...initState
  });

  const [options, setOptions] = useState(initState.options || []);

  const isOpen = !!ChatGPTPersonalAnalysis && !closed;

  const teamShortNames = team && team.map(team => team.teamShortName);
  const isChubbOrTestTeam =
    teamShortNames &&
    (teamShortNames.includes('CBB') ||
      teamShortNames.includes('YRTeam') ||
      teamShortNames.includes('CBBTrainer') ||
      teamShortNames.includes('pfplus'));
  const isPriorityLazypackEnabled = !!hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_CHUBB_AI_LAZYPACK');
  const shouldProvideChubbLazyPack = isChubbOrTestTeam && isPriorityLazypackEnabled;

  const handleTraitsChange = (newTraits, disc) => {
    setFormValues(prev => ({ ...prev, personality_traits: newTraits, personality: disc }));
  };

  const onValueChange = (field, newValue) => {
    setFormValues(prev => ({ ...prev, [field]: newValue }));
  };

  useEffect(() => {
    if (client?.factsheetId?.personality) {
      const existingPersonality = client.factsheetId.personality;
      const defaultTraits = { outgoing: false, introverted: false, logical: false, emotional: false };
      setFormValues(prev => ({
        ...prev,
        personality: existingPersonality,
        personality_traits: { ...defaultTraits, ...getTraitsFromDisc(existingPersonality) }
      }));
    }
    if (client?.factsheetId?.promptInput) {
      const pi = client.factsheetId.promptInput;
      setFormValues(prev => ({
        ...prev,
        target: pi.financialTargets || [],
        interested: pi.financialInterests || [],
        lifeStage: pi.lifeStage || '',
        hobbies: pi.topics || []
      }));
    }
  }, [client?.factsheetId?.personality]);

  useEffect(() => {
    if (payload) {
      setFormValues(prev => ({
        ...prev,
        remark: payload?.remark || undefined,
        ...(payload?.options || {}),
        ...(payload?.product || {}),
        ...(payload?.traits || {})
      }));
    }
  }, [payload]);

  const onClose = () => {
    setFormValues({});
    setOptions([]);
    toggleControl('ChatGPTPersonalAnalysis', { ...ChatGPTPersonalAnalysis, closed: true });
  };

  const onExited = () => {
    toggleControl('ChatGPTPersonalAnalysis', false);
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: client,
      extraContent: options.toString(),
      onSubmit: () => {
        resetGPTQuota();
        forceUpdate();
      }
    });
  };

  const completeAction = result => {
    try {
      setOptions(JSON.parse(result.replace('```json', '').replace('```', '')));
    } catch {
      setOptions([]);
    }
  };

  const getPromptIdentifier = (type, client) => {
    let promptIdentifier = 'match_hot_topic';
    switch (type) {
      case 'product':
        promptIdentifier += ':product';
        break;
      case 'meeting_notes':
        promptIdentifier += ':meeting_notes';
        break;
      default:
        promptIdentifier += ':general';
        break;
    }
    if (client) {
      promptIdentifier += ':client';
    }
    if (shouldProvideChubbLazyPack) {
      promptIdentifier += ':cbb';
    }

    const validIdentifier = IdentifierWhitelist.includes(promptIdentifier);
    return validIdentifier ? promptIdentifier : undefined;
  };

  const send = async () => {
    try {
      if (type === 'general' && !formValues.chatPurpose) {
        toast.info(IconToast('error', intl.formatMessage({ id: 'select-chat-purpose' })), { className: 'new-toast' });
        return;
      }
      const promptIdentifier = getPromptIdentifier(type, client);

      if (!promptIdentifier) {
        toast.info(IconToast('error', intl.formatMessage({ id: 'invalid-prompt-identifier' })), {
          className: 'new-toast'
        });
        return;
      }

      validateGPTQuota();
      setLoading(true);
      trackDirect('useAiFeature');
      trackDirect('suggestHotTopic');
      tracking('AI: ChatGPT Personal Analysis');
      saveToClient();

      // Prompt Identifier: match_hot_topic:type:client:team
      // For example: match_hot_topic:product:client:CBB
      // Option Type: product, meeting_notes, general
      // Client: Client or empty
      // Team: Team or empty
      let promptData = {
        ...formValues,
        language: formValues.language || intl.locale || 'Traditional Chinese'
      };
      if (client) {
        promptData = {
          ...promptData,
          client_name: client.factsheetId.name,
          client_personality: client.factsheetId.personality,
          client_tags: client.factsheetId.tags && client.factsheetId.tags.map(tag => tag.text),
          client_gender: formValues.gender || client.factsheetId.gender,
          client_marriage_status: client.factsheetId.marriageStatus,
          client_child: client.factsheetId.child,
          client_monthly_income: client.factsheetId.monthlyIncome
        };
      }
      await vertexGenerateWithTextStreaming(promptIdentifier, promptData, [], null, completeAction, setLoading);

      decreaseGPTQuota();
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  const goToLazyPack = (id, message) => {
    history.push({
      pathname: history.location.pathname,
      state: { options, ...formValues }
    });
    history.push({
      pathname: '/hot_topic',
      hash: `#${id}`,
      state: { share_message: message, language: formValues.language }
    });
  };

  const saveToClient = () => {
    if (client) {
      const fsUpdates = client.factsheetId;
      const promptInput = fsUpdates.promptInput || {};
      promptInput.financialTargets = formValues.target;
      promptInput.financialInterests = formValues.interested;
      promptInput.lifeStage = formValues.lifeStage;
      promptInput.topics = formValues.hobbies;

      updateFactSheet({
        id: fsUpdates._id,
        key: 'promptInput',
        value: promptInput,
        clientId: client._id
      });
      updateFactSheet({
        id: fsUpdates._id,
        key: 'personality',
        value: formValues.personality,
        clientId: client._id
      });
    }
  };

  const text = options
    .map(
      (option, index) =>
        `${intl.formatMessage({ id: 'option-number' }, { number: index + 1 })}\n` +
        `${intl.formatMessage({ id: 'lazy-pack-title' })}: ${option.title}\n` +
        `${intl.formatMessage({ id: 'reason' })}: ${option.reason}\n\n` +
        `${intl.formatMessage({ id: 'personalized-message' })}: ${option.personalized_message}\n`
    )
    .join('\n\n');

  useEffect(() => {
    if (client) {
      setFormValues(prev => ({ ...prev, gender: client.factsheetId.gender || '' }));
    }
  }, [client]);

  return (
    <Fragment>
      <LoadingBackdrop open={loading} />
      <ChatGPTPresetDrawer
        open={isOpen}
        onClose={onClose}
        onExited={onExited}
        onFeedback={onFeedback}
        title={intl.formatMessage({ id: 'chat-gpt-options-lazy-packs-text-button-label' })}
        subtitle={intl.formatMessage({ id: 'gpt-subtitle-lazy-packs' })}
        showQuota
      >
        <MatchLazyPacks
          type={type}
          intl={intl}
          inputOptions={formValues}
          onValueChange={onValueChange}
          onTraitsChange={handleTraitsChange}
          client={client}
          onSend={send}
          options={options}
          onGoToLazyPack={goToLazyPack}
          text={text}
        />
      </ChatGPTPresetDrawer>
    </Fragment>
  );
};

const container = connect(
  state => ({
    ChatGPTPersonalAnalysis: state.control.ChatGPTPersonalAnalysis,
    userDetails: state.user.userDetails,
    teamCampaignCodes: state.functions.campaignCodes,
    team: state.team.teams
  }),
  {
    toggleControl,
    updateFactSheet
  }
)(ChatGPTPersonalAnalysis);

export default injectIntl(withRouter(container));
